import {
  type AlertBanner,
  BannerType,
  PersistenceBehaviour,
} from '@generated/graphql';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import NewReleases from '@mui/icons-material/NewReleases';
import WarningIcon from '@mui/icons-material/Warning';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { colorMap } from '@ovComponents/2-component/badge/badge';
import {
  AlertBannerStatus,
  type AlertBannerTable,
  type BannerTypeOption,
  type ConfigurationField,
} from './types';

export const DEFAULT_PAGE_SIZE = 15;

export const stateColorMap: Record<AlertBannerStatus, string> = {
  [AlertBannerStatus.ACTIVE]: colorMap.positive,
  [AlertBannerStatus.SCHEDULED]: colorMap.neutral,
  [AlertBannerStatus.EXPIRED]: colorMap.gray,
  [AlertBannerStatus.DRAFT]: colorMap.supportThree,
};

export const DEFAULT_BANNER_VALUES: Pick<
  AlertBanner,
  'bannerCopy' | 'configuration' | 'persistenceBehaviour'
> = {
  bannerCopy: getLanguageDefaults(),
  configuration: {
    displayOnWeb: true,
    displayOnIOS: true,
    displayOnAndroid: true,
    makeDismissible: false,
    enableAction: false,
  },
  persistenceBehaviour: PersistenceBehaviour.EverySession,
};

export enum TableHeaderKeys {
  Name = 'name',
  RoleProfiles = 'roleProfiles',
  LastUpdated = 'lastUpdated',
  ActiveDateRange = 'activeDateRange',
  Status = 'status',
  Organization = 'organization',
  Actions = 'actions',
}

export const sortableTableHeaderKeyMap: Partial<
  Record<TableHeaderKeys, string>
> = {
  [TableHeaderKeys.Name]: 'name',
  [TableHeaderKeys.LastUpdated]: 'updatedAt',
  [TableHeaderKeys.ActiveDateRange]: 'startDate',
  [TableHeaderKeys.Status]: 'state',
};

export const BASE_TABLE: AlertBannerTable = [
  {
    key: TableHeaderKeys.Name,
    isSortable: true,
    labelPath: `table.${TableHeaderKeys.Name}`,
  },
  {
    key: TableHeaderKeys.RoleProfiles,
    isSortable: false,
    labelPath: `table.${TableHeaderKeys.RoleProfiles}`,
  },
  {
    key: TableHeaderKeys.LastUpdated,
    isSortable: true,
    labelPath: `table.${TableHeaderKeys.LastUpdated}`,
  },
  {
    key: TableHeaderKeys.ActiveDateRange,
    isSortable: true,
    labelPath: `table.${TableHeaderKeys.ActiveDateRange}`,
  },
  {
    key: TableHeaderKeys.Status,
    isSortable: true,
    labelPath: `table.${TableHeaderKeys.Status}.title`,
  },
  {
    key: TableHeaderKeys.Organization,
    isSortable: false,
    labelPath: `table.${TableHeaderKeys.Organization}`,
  },
  {
    key: TableHeaderKeys.Actions,
    isSortable: false,
    labelPath: '',
    permissions: ['write:alert_banners', 'transition:alert_banners'],
  },
];

export const BANNER_TYPE_OPTIONS: BannerTypeOption[] = [
  {
    key: BannerType.Info,
    icon: NewReleases,
    backgroundColor: 'primary20',
    iconColor: 'primary50',
  },
  {
    key: BannerType.Success,
    icon: CheckCircleIcon,
    backgroundColor: 'positive40',
    iconColor: 'positive60',
  },
  {
    key: BannerType.Warning,
    icon: WarningIcon,
    backgroundColor: 'warning40',
    iconColor: 'warning70',
  },
  {
    key: BannerType.Urgent,
    icon: ErrorIcon,
    backgroundColor: 'negative40',
    iconColor: 'negative70',
  },
];

export const CONFIGURATION_FIELDS: ConfigurationField[] = [
  'displayOnWeb',
  'displayOnIOS',
  'displayOnAndroid',
  'makeDismissible',
  'enableAction',
];

export const PERSISTENCE_BEHAVIOURS: PersistenceBehaviour[] = [
  PersistenceBehaviour.FirstSession,
  PersistenceBehaviour.EverySession,
  PersistenceBehaviour.UntilDismissed,
  PersistenceBehaviour.UntilActioned,
];
